/**
 * @license
 * Copyright 2018-2021 Streamlit Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from "react"
import { withTheme } from "emotion-theming"
import { ChevronDown } from "baseui/icon"
import { Select as UISelect, OnChangeParams, Option } from "baseui/select"
import { logWarning } from "src/lib/log"
import { Theme } from "src/theme"
import { VirtualDropdown } from "src/components/shared/Dropdown"
import { hasMatch, score } from "fzy.js"
import _ from "lodash"
import { Placement } from "src/components/shared/Tooltip"
import TooltipIcon from "src/components/shared/TooltipIcon"
import {
  StyledWidgetLabel,
  StyledWidgetLabelHelpInline,
} from "src/components/widgets/BaseWidget"

export interface Props {
  disabled: boolean
  width?: number
  value: number
  onChange: (value: number) => void
  options: any[]
  label?: string
  help?: string
  theme: Theme
}

interface State {
  /**
   * The value specified by the user via the UI. If the user didn't touch this
   * widget's UI, the default value is used.
   */
  value: number
}

interface SelectOption {
  label: string
  value: string
}

// Add a custom filterOptions method to filter options only based on labels.
// The baseweb default method filters based on labels or indeces
// More details: https://github.com/streamlit/streamlit/issues/1010
// Also filters using fuzzy search powered by fzy.js. Automatically handles
// upper/lowercase.
export function fuzzyFilterSelectOptions(
  options: SelectOption[],
  pattern: string
): readonly SelectOption[] {
  if (!pattern) {
    return options
  }

  return _(options)
    .filter((opt: SelectOption) => hasMatch(pattern, opt.label))
    .sortBy((opt: SelectOption) => score(pattern, opt.label))
    .reverse()
    .value()
}

class Selectbox extends React.PureComponent<Props, State> {
  public state: State = {
    value: this.props.value,
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (
      prevProps.value !== this.props.value &&
      this.state.value !== this.props.value
    ) {
      this.setState({ value: this.props.value })
    }
  }

  private onChange = (params: OnChangeParams): void => {
    if (params.value.length === 0) {
      logWarning("No value selected!")
      return
    }

    const [selected] = params.value

    this.setState({ value: parseInt(selected.value, 10) }, () =>
      this.props.onChange(this.state.value)
    )
  }

  private filterOptions = (
    options: readonly Option[],
    filterValue: string
  ): readonly Option[] =>
    fuzzyFilterSelectOptions(options as SelectOption[], filterValue)

  private renderLabel = (): React.ReactElement | null => {
    const { label, help } = this.props
    if (!label) {
      return null
    }

    return (
      <StyledWidgetLabel>
        {label}
        {help && (
          <StyledWidgetLabelHelpInline>
            <TooltipIcon content={help} placement={Placement.TOP_RIGHT} />
          </StyledWidgetLabelHelpInline>
        )}
      </StyledWidgetLabel>
    )
  }

  public render = (): React.ReactNode => {
    const style = { width: this.props.width }
    const { theme } = this.props
    let { disabled, options } = this.props

    const value = [
      {
        label:
          options.length > 0
            ? options[this.state.value]
            : "No options to select.",
        value: this.state.value.toString(),
      },
    ]

    if (options.length === 0) {
      options = ["No options to select."]
      disabled = true
    }

    const selectOptions: SelectOption[] = []
    options.forEach((option: string, index: number) =>
      selectOptions.push({
        label: option,
        value: index.toString(),
      })
    )

    return (
      <div className="row-widget stSelectbox" style={style}>
        {this.renderLabel()}
        <UISelect
          clearable={false}
          disabled={disabled}
          labelKey="label"
          onChange={this.onChange}
          options={selectOptions}
          filterOptions={this.filterOptions}
          value={value}
          valueKey="value"
          overrides={{
            Root: {
              style: {
                border: `2px solid ${theme.colors.lightGray}`,
                borderRadius: "0.375rem",
                boxShadow: `0 0 black, 0 0 black, 0 1px 2px 0 rgba(0, 0, 0, 0.05)`,
              },
            },
            ControlContainer: {
              style: {
                backgroundColor: theme.colors.inputBgColor,
                borderColor: `${theme.colors.inputBgColor} !important`,
              },
            },
            ValueContainer: {
              style: {
                paddingTop: "6px",
                paddingBottom: "6px",
              },
            },
            SelectArrow: {
              component: () => <ChevronDown size={24} color="#6d7582" />,
            },
            Dropdown: { component: VirtualDropdown },
          }}
        />
      </div>
    )
  }
}

export default withTheme(Selectbox)
